import { Session } from '@supabase/supabase-js';
import { ensureSupabaseConfigured } from '../lib/supabaseClient';
import { Post, PostUpsertInput } from '../types';

const POSTS_BUCKET = 'post-images';

interface SupabasePostRow {
  id: string;
  title: string;
  description: string;
  image_path: string;
  date: string;
  link_fb: string | null;
  link_instagram: string | null;
  published: boolean;
  created_at: string;
  updated_at: string;
}

function normalizeOptionalText(value?: string) {
  const normalized = value?.trim();
  return normalized ? normalized : null;
}

function mapRowToPost(row: SupabasePostRow): Post {
  const client = ensureSupabaseConfigured();
  const { data } = client.storage.from(POSTS_BUCKET).getPublicUrl(row.image_path);

  return {
    id: row.id,
    title: row.title,
    description: row.description,
    image: data.publicUrl,
    image_path: row.image_path,
    date: row.date,
    link_fb: row.link_fb || undefined,
    link_instagram: row.link_instagram || undefined,
    published: row.published,
    created_at: row.created_at,
    updated_at: row.updated_at,
    source: 'supabase',
  };
}

export async function fetchPublishedPosts(): Promise<Post[]> {
  const client = ensureSupabaseConfigured();

  const { data, error } = await client
    .from('posts')
    .select('*')
    .eq('published', true)
    .order('date', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return (data as SupabasePostRow[]).map(mapRowToPost);
}

export async function fetchAdminPosts(): Promise<Post[]> {
  const client = ensureSupabaseConfigured();

  const { data, error } = await client
    .from('posts')
    .select('*')
    .order('date', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return (data as SupabasePostRow[]).map(mapRowToPost);
}

export async function uploadPostImage(file: File): Promise<string> {
  const client = ensureSupabaseConfigured();
  const randomId = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const safeFileName = file.name.toLowerCase().replace(/[^a-z0-9._-]/g, '-');
  const path = `posts/${randomId}-${safeFileName}`;

  const { error } = await client.storage
    .from(POSTS_BUCKET)
    .upload(path, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (error) {
    throw error;
  }

  return path;
}

export async function createPost(input: PostUpsertInput): Promise<Post> {
  const client = ensureSupabaseConfigured();

  const payload = {
    title: input.title.trim(),
    description: input.description.trim(),
    image_path: input.image_path,
    date: input.date,
    link_fb: normalizeOptionalText(input.link_fb),
    link_instagram: normalizeOptionalText(input.link_instagram),
    published: input.published,
  };

  const { data, error } = await client
    .from('posts')
    .insert(payload)
    .select('*')
    .single();

  if (error) {
    throw error;
  }

  return mapRowToPost(data as SupabasePostRow);
}

export async function updatePost(id: string, input: PostUpsertInput): Promise<Post> {
  const client = ensureSupabaseConfigured();

  const payload = {
    title: input.title.trim(),
    description: input.description.trim(),
    image_path: input.image_path,
    date: input.date,
    link_fb: normalizeOptionalText(input.link_fb),
    link_instagram: normalizeOptionalText(input.link_instagram),
    published: input.published,
  };

  const { data, error } = await client
    .from('posts')
    .update(payload)
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    throw error;
  }

  return mapRowToPost(data as SupabasePostRow);
}

export async function deletePost(id: string): Promise<void> {
  const client = ensureSupabaseConfigured();

  const { error } = await client
    .from('posts')
    .delete()
    .eq('id', id);

  if (error) {
    throw error;
  }
}

export async function signInAdmin(email: string, password: string): Promise<void> {
  const client = ensureSupabaseConfigured();

  const { error } = await client.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    throw error;
  }
}

export async function signOutAdmin(): Promise<void> {
  const client = ensureSupabaseConfigured();
  const { error } = await client.auth.signOut();

  if (error) {
    throw error;
  }
}

export async function getSession(): Promise<Session | null> {
  const client = ensureSupabaseConfigured();
  const { data, error } = await client.auth.getSession();

  if (error) {
    throw error;
  }

  return data.session;
}

export function onAuthStateChange(callback: (session: Session | null) => void) {
  const client = ensureSupabaseConfigured();
  const { data } = client.auth.onAuthStateChange((_event, session) => {
    callback(session);
  });

  return data.subscription;
}

export async function checkCurrentUserIsAdmin(): Promise<boolean> {
  const client = ensureSupabaseConfigured();
  const { data: authData, error: authError } = await client.auth.getUser();

  if (authError) {
    throw authError;
  }

  const userId = authData.user?.id;
  if (!userId) {
    return false;
  }

  const { data, error } = await client
    .from('admin_users')
    .select('user_id')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return Boolean(data?.user_id);
}
